import {makeKebabCase} from '@/utils/helpers/string';
import React, {useMemo} from 'react';
import RadioButton from '../../../atoms/RadioButton/RadioButton';
import List from '../List';
import {StyledGroupName, StyledSublist} from '../styles';
import Props, {Selection} from './typings';

const RadioList = ({options, selected, group, style = 'text', callback}: Props) => {
  const defaultOption = 'all';
  const checkedOptions = useMemo(
    () =>
      selected.flatMap((item) =>
        item.subcategories?.length
          ? [item.value, ...item.subcategories.map((sub) => sub.value)]
          : [item.value],
      ),
    [selected],
  );

  const optionIsSelected = (option) => {
    if (
      (checkedOptions.length === 1 && selected[0].value === group) ||
      checkedOptions.length === 0
    ) {
      return option === defaultOption;
    } else {
      return checkedOptions.includes(option);
    }
  };

  /**
   * Combines the sub-selection with the parent option.
   * Example of value for callback: [{value: prints, subcategories: [{value: prints-giclee}] }]
   * @param {Selection[]} subSelection - The sub-selection array.
   * @param {string | number} parent - The parent option value.
   */
  const combineResponse = (subSelection: Selection[], parent: string | number) => {
    if (subSelection.length === 0) {
      // If no sub-selection is made, just return the parent option
      callback([{value: parent}]);
    } else if (!subSelection[0].subcategories) {
      // If there are no subcategories yet, combine parent with sub-selection
      callback([{value: parent, subcategories: subSelection}]);
    } else {
      // If there are subcategories, no need to combine any further
      callback(subSelection);
    }
  };

  const buttonResponse = (resp) => {
    if (resp === defaultOption) {
      callback([]);
    } else {
      callback([{value: resp}]);
    }
  };

  return (
    <>
      {options.map((option) => (
        <React.Fragment key={`${group}-${option.value ?? makeKebabCase(option.label)}`}>
          {option.value ? (
            <RadioButton
              checked={optionIsSelected(option.value)}
              label={option.label}
              value={option.value}
              group={group}
              callback={buttonResponse}
              style={style}
            />
          ) : (
            <StyledGroupName>{option.label}</StyledGroupName>
          )}

          {option.sublist && (
            <StyledSublist $visible={!option.value ? true : checkedOptions.includes(option.value)}>
              <List
                type={option.sublist.type}
                style={option.sublist.style}
                group={option.value ? option.value.toString() : group}
                selected={selected}
                options={option.sublist.options}
                callback={(resp) => combineResponse(resp, option.value ?? group)}
              />
            </StyledSublist>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default RadioList;
